/**
 * ContentGalleryListWrapper - Composite React Component
 *
 * This component combines MultipleColumnContent and Pagination from the Kohler DSM library
 * to render a paginated, multi-column content gallery for the AEM content-gallery-list DSM module.
 *
 * Features:
 * - Displays cards in a multi-column layout with pagination controls
 * - Supports dynamic per-page selection and "Load More" functionality
 * - Handles empty states and smooth scroll to top
 *
 * Props:
 * @param {Object} props - Component props
 * @param {string} props.componentType - Component type identifier (for data attributes)
 * @param {Object} props.multiColumnContent - Props for MultipleColumnContent (expects a 'cards' array)
 * @param {Object} props.pagination - Props for Pagination (expects perPageCTAs, loadMoreCTA, etc.)
 */

'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { MultipleColumnContent } from '@kohler-dsm-library/kohler-modules';
import { Pagination } from '@kohler-dsm-library/kohler-core';
import { enhancePaginationConfig } from '@/aem-dsm-mappers/utils/enhancePaginationConfig';

const ContentGalleryListWrapper = ({ componentType, multiColumnContent = {}, pagination }) => {
  // Keep original cards
  const originalCards = multiColumnContent.cards || [];
  // Determine default perPage value
  const defaultPerPage = pagination?.perPageCTAs?.[0]?.value || 30;
  const [perPage, setPerPage] = useState(defaultPerPage);
  const [loadedCount, setLoadedCount] = useState(defaultPerPage);

  // Update loadedCount if perPage changes
  useEffect(() => {
    if (perPage === 'all') {
      setLoadedCount('all');
    } else {
      setLoadedCount(Number(perPage));
    }
  }, [perPage]);

  useEffect(() => {
    if (loadedCount === 'all') {
      setPerPage('all');
    } else {
      setPerPage(Number(loadedCount));
    }
  }, [loadedCount]);

  const paginatedCards = useMemo(() => {
    if (loadedCount === 'all') {
      return originalCards;
    }
    return originalCards.slice(0, Number(loadedCount));
  }, [originalCards, loadedCount]);

  // Handle empty states
  if (!multiColumnContent && !pagination) {
    console.warn('ContentGalleryList: No multiColumnContent or pagination props provided');
    return null;
  }

  // Handler for per-page click events
  const handlePerPageClick = (value) => {
    setPerPage(value);
  };

  // Handle for load more click events
  const handleLoadMoreClick = () => {
    setLoadedCount((prev) => {
      const next = Number(prev) + Number(perPage);
      if (next >= originalCards.length) {
        return 'all';
      }
      return next;
    });
  };

  // Handle for back to top click events
  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Hide loadMoreCTA if perPage or loadedCount is 'all'
  const shouldHideLoadMore = perPage === 'all' || loadedCount === 'all';
  const paginationConfig = shouldHideLoadMore
    ? { ...pagination, loadMoreCTA: undefined }
    : pagination;

  const paginationProps = enhancePaginationConfig(
    paginationConfig,
    perPage,
    handlePerPageClick,
    handleLoadMoreClick,
    handleBackToTop,
  );

  // Pass paginatedCards to MultipleColumnContent
  const multiColumnContentProps = {
    ...multiColumnContent,
    cards: paginatedCards,
  };

  return (
    <div className={`content-gallery-list`} data-component-type={componentType}>
      {/* Multi-column content section */}
      {multiColumnContent && Object.keys(multiColumnContent).length > 0 && (
        <div className="content-gallery-list__content">
          <MultipleColumnContent {...multiColumnContentProps} />
        </div>
      )}

      {/* Pagination section */}
      {paginationProps && Object.keys(paginationProps).length > 0 && (
        <div
          className="content-gallery-list__pagination"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            padding: '10px 25px 25px',
            gap: '25px',
            width: '100%',
          }}
        >
          <Pagination {...paginationProps} />
        </div>
      )}
    </div>
  );
};

export default ContentGalleryListWrapper;
